// Session model for VERA — the RBT "My Sessions" workflow.
// Stored under Firestore: companies/{companyId}/sessions/{sessionId}

export type SessionType = 'direct-therapy' | 'supervision' | 'parent-training';

export type SessionStatus =
  | 'scheduled'       // not started yet (upcoming)
  | 'in-progress'     // session currently active
  | 'pending-notes'   // session ended, notes not yet submitted
  | 'submitted'       // notes submitted, awaiting BCBA review
  | 'approved'        // BCBA signed off
  | 'needs-revision'  // BCBA sent it back with comments
  | 'revised';        // RBT resubmitted after revision

export interface GpsCheckIn {
  lat: number;
  lng: number;
  timestamp: Date | null;
  verified: boolean;
}

// A recorded "Privacy Mode" window during an active session — capture is
// paused between startTime and endTime (endTime null while still paused).
export interface PrivacyPause {
  startTime: Date | null;
  endTime: Date | null;
}

export interface Session {
  id: string;
  companyId: string;
  appointmentId?: string | null; // links back to the originating appointment

  // Participants
  clientId: string;
  clientName: string;
  rbtId: string;
  rbtName: string;
  supervisorId: string;
  supervisorName: string;

  // Scheduling
  scheduledStart: Date;
  scheduledEnd: Date;
  actualStart: Date | null;
  actualEnd: Date | null;

  // Lifecycle
  status: SessionStatus;
  sessionType: SessionType;
  clientAddress: string;

  // Documentation
  dictationTranscript: string;
  dictationAudioUrl: string;
  noteData: Record<string, any> | null;
  noteTemplate: string;
  submittedAt: Date | null;
  deadline: Date | null; // calculated from company settings when session ends
  supervisorComments: string;

  // BCBA review outcome
  supervisorSignature?: string;
  approvedAt?: Date | null;
  revisionRequestedAt?: Date | null;
  parentSummary?: string;
  parentSummaryApprovedAt?: Date | null;
  parentSummaryEditedByBCBA?: boolean;
  notePdfUrl?: string; // Firebase Storage URL of the generated clinical PDF

  // Verification
  gpsCheckIn: GpsCheckIn | null;
  verificationPhotoUrl: string;

  // Privacy Mode windows logged during the session (HIPAA/compliance trail)
  privacyPauses?: PrivacyPause[];

  createdAt: Date;
  updatedAt: Date;
}

export const SESSION_TYPE_LABELS: Record<SessionType, string> = {
  'direct-therapy': 'Direct Therapy',
  supervision: 'Supervision',
  'parent-training': 'Parent Training',
};

// Statuses that belong in the "Completed" section (notes submitted in some form)
export const COMPLETED_STATUSES: SessionStatus[] = [
  'submitted',
  'approved',
  'needs-revision',
  'revised',
];
